import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';

import { isAmericanBarrierHit } from '../utils/barrierStyle';

interface Underlying {
  strikeDistance: number;
  barrierDistance: number;
  couponBarrierDistance: number | null;
}

interface ProductBarrierInfoProps {
  barrierStyleName: string;
  /** Desde tbld_ProductCheck.PROCHBarrierYN (hit permanente American/Intraday). */
  barrierHitRecorded?: boolean;
  strikeLevel: number | null;
  barrierLevel: number | null;
  underlyings: Underlying[];
  couponMemory?: boolean | null;
  showCouponMemory?: boolean;
  showDistanceCouponBarrier?: boolean;
  barrierProps?: Record<string, boolean>;
  sectionTitle?: string; // Título opcional de la sección
  couponBarrier?: number | null;
  showStoppLoss?: boolean;
  stopLoss: number | null;
  showDistanceStopLoss?: boolean;
  distanceStopLoss: number | null;
}

export const ProductBarrierInfo: React.FC<ProductBarrierInfoProps> = ({
  barrierStyleName,
  barrierHitRecorded,
  strikeLevel,
  barrierLevel,
  underlyings,
  barrierProps = {},
  sectionTitle,
  couponMemory,
  showCouponMemory,
  showDistanceCouponBarrier,
  couponBarrier,
  showStoppLoss,
  stopLoss,
  showDistanceStopLoss,
  distanceStopLoss,
}) => {
  // Find the worst-off underlying for barrier (lowest barrierDistance)
  const worstOffBarrierUnderlying = underlyings.length > 0
    ? underlyings.reduce((worst, current) => 
        current.barrierDistance < worst.barrierDistance ? current : worst
      )
    : null;

  // Find the worst-off underlying for strike (lowest strikeDistance)
  const worstOffStrikeUnderlying = underlyings.length > 0
    ? underlyings.reduce((worst, current) => 
        current.strikeDistance < worst.strikeDistance ? current : worst
      )
    : null;

  // Helper function to check if a prop should be shown (defaults to true if not specified)
  const shouldShow = (propName: string): boolean => {
    return barrierProps[propName] !== false;
  };

  const barrierHitIsYes = isAmericanBarrierHit(
    barrierStyleName,
    barrierHitRecorded,
    worstOffBarrierUnderlying?.barrierDistance ?? null,
  );

  // Check if there are any visible fields
  const hasVisibleFields = 
    shouldShow('barrierType') ||
    shouldShow('strikeLevel') ||
    shouldShow('strikeDistance') ||
    shouldShow('barrierLevel') ||
    shouldShow('barrierDistance') ||
    shouldShow('barrierHit') ||
    shouldShow('couponBarrier');

  // If no fields are visible, don't render the component
  if (!hasVisibleFields) {
    return null;
  }

  return (
    <View style={[styles.container, Platform.OS === 'web' && { padding: 16, marginBottom: 12 }]}>
      <Text style={styles.sectionTitle}>{sectionTitle || 'Barrier'}</Text>
      <View style={styles.separator} />

      <View style={styles.grid}>
        <View style={styles.row}>
          {shouldShow('barrierType') && (
          <View style={styles.item}>
            <Text style={styles.label}>Barrier type</Text>
            <Text style={styles.value}>{barrierStyleName || 'No barrier'}</Text>
          </View>
          )}
          {shouldShow('strikeLevel') && (
          <View style={styles.item}>
            <Text style={styles.label}>Strike level</Text>
            <Text style={styles.value}>
              {strikeLevel ? `${strikeLevel.toFixed(0)}%` : '-'}
            </Text>
          </View>
          )}
        </View>

        <View style={styles.row}>
          {shouldShow('strikeDistance') && (
          <View style={styles.item}>
            <Text style={styles.label}>Strike distance (worst-of)</Text>
            <Text
              style={[
                styles.value,
                (worstOffStrikeUnderlying?.strikeDistance ?? 0) < 0 && styles.negativeValue,
              ]}
            >
              {((worstOffStrikeUnderlying?.strikeDistance || 0) * 100).toFixed(2)}%
            </Text>
          </View>
          )}
          {shouldShow('barrierLevel') && (
          <View style={styles.item}>
            <Text style={styles.label}>Barrier</Text>
            <Text style={styles.value}>
              {barrierLevel ? `${barrierLevel.toFixed(0)}%` : '-'}
            </Text>
          </View>
          )}
        </View>

        <View style={styles.row}>
          {shouldShow('barrierDistance') && (
          <View style={styles.item}>
            <Text style={styles.label}>Distance From Barrier (worst-of)</Text>
            <Text
              style={[
                styles.value,
                (worstOffBarrierUnderlying?.barrierDistance ?? 0) < 0 && styles.negativeValue,
              ]}
            >
              {worstOffBarrierUnderlying?.barrierDistance?.toFixed(2) || '0.00'}%
            </Text>
          </View>
          )}
          {shouldShow('barrierHit') && (
          <View style={styles.item}>
            <Text style={styles.label}>Barrier hit</Text>
            <Text style={styles.value}>
              {barrierHitIsYes ? 'YES' : 'NO'}
            </Text>
          </View>)}
        </View>

        {/* Coupon Barrier row */}
        {(shouldShow('couponBarrier') || showCouponMemory || showDistanceCouponBarrier) && (
        <View style={styles.row}>
          {shouldShow('couponBarrier') && couponBarrier != null && (
            <View style={styles.item}>
              <Text style={styles.label}>Coupon Barrier</Text>
              <Text style={styles.value}>
                {couponBarrier ? `${couponBarrier.toFixed(2)}%` : '-'}
              </Text>
            </View>
          )}
          {showCouponMemory && (
            <View style={styles.item}>
              <Text style={styles.label}>Coupon Memory</Text>
              <Text style={styles.value}>
                {couponMemory ? `YES` : 'NO'}
              </Text>
            </View>
          )}
        </View>
        )}

        {/* Coupon Barrier Distance row */}
        {showDistanceCouponBarrier && worstOffBarrierUnderlying?.couponBarrierDistance != null && (
        <View style={styles.row}>
          <View style={styles.item}>
            <Text style={styles.label}>Coupon barrier distance (worst-of)</Text>
            <Text
              style={[
                styles.value,
                (worstOffBarrierUnderlying?.couponBarrierDistance ?? 0) < 0 && styles.negativeValue,
              ]}
            >
              {(worstOffBarrierUnderlying?.couponBarrierDistance || 0).toFixed(2)}%
            </Text>
          </View>
        </View>
        )}

        {/* Stop Loss row */}
        {(shouldShow('stopLoss') || shouldShow('distanceStopLoss')) && (
        <View style={styles.row}>
          {shouldShow('stopLoss') && stopLoss != null && (
            <View style={styles.item}>
              <Text style={styles.label}>Stop Loss</Text>
              <Text style={styles.value}>{stopLoss ? `${stopLoss.toFixed(2)}` : '-'}</Text>
            </View>
          )}
          {shouldShow('distanceStopLoss') && distanceStopLoss != null && (
            <View style={styles.item}>
              <Text style={styles.label}>Distance Stop Loss</Text>
              <Text style={styles.value}>
                {distanceStopLoss ? `${distanceStopLoss.toFixed(2)}%` : '-'}
              </Text>
            </View>
          )}
        </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  separator: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginBottom: 16,
  },
  grid: {
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
  },
  item: {
    flex: 1,
  },
  label: {
    fontSize: 15,
    color: '#757575',
    marginBottom: 4,
  },
  value: {
    fontSize: 14,
    fontWeight: '600',
    color: '#212121',
  },
  negativeValue: {
    color: '#F44336',
  },
});
